import { PlusOutlined } from '@ant-design/icons';
import { App, Button, Form, Select } from 'antd';
import { createContext, useContext } from 'react';
import { IncomeType } from '../../../../api/btlApplicationForm/types';
import type { MessageKeys, TypedFormatMessageFn } from '../../../../types/intl';

export const IncomeListItemIndexContext = createContext<number | null>(null);

const MAX_EMPLOYMENT_INCOMES = 5;

const EMPLOYMENT_TYPE_LABEL_IDS: Record<IncomeType, MessageKeys> = {
  [IncomeType.PAYE_EMPLOYEE]: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.0.label',
  [IncomeType.SOLE_TRADER]: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.1.label',
  [IncomeType.CONTRACTOR]: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.2.label',
  [IncomeType.PARTNERSHIP]: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.3.label',
  [IncomeType.SALARIED_DIRECTOR]: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.4.label',
  [IncomeType.UNEMPLOYED]: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.5.label',
  [IncomeType.RETIRED]: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.6.label',
};

export function isNonWorkingEmploymentType(employmentType: unknown): boolean {
  return employmentType === IncomeType.UNEMPLOYED || employmentType === IncomeType.RETIRED;
}

function employmentTypeOptions(includeNonWorking: boolean, formatMessage: TypedFormatMessageFn) {
  return Object.values(IncomeType)
    .filter(type => includeNonWorking || !isNonWorkingEmploymentType(type))
    .map(type => ({
      value: type,
      label: formatMessage({ id: EMPLOYMENT_TYPE_LABEL_IDS[type] }),
    }));
}

export function MemberEmploymentTypeSelect({
  value,
  disabled,
  isPrimaryApplicant,
  formatMessage,
}: {
  value?: IncomeType;
  disabled?: boolean;
  isPrimaryApplicant: boolean;
  formatMessage: TypedFormatMessageFn;
}) {
  const form = Form.useFormInstance();
  const { modal } = App.useApp();
  const listIndex = useContext(IncomeListItemIndexContext) ?? 0;
  const isPrimaryIncome = listIndex === 0;
  const includeNonWorking = !isPrimaryApplicant && isPrimaryIncome;
  const selectedEmploymentType = Form.useWatch(['incomes', listIndex, 'employmentType'], form) ?? value;

  function setEmploymentType(nextValue: IncomeType) {
    form.setFields([
      {
        name: ['incomes', listIndex, 'employmentType'],
        value: nextValue,
        errors: [],
      },
    ]);
  }

  function applyNonWorkingPrimaryIncome(nextValue: IncomeType) {
    const incomes: Record<string, unknown>[] = form.getFieldValue('incomes') ?? [];
    const [primary] = incomes;

    form.setFieldsValue({
      incomes: [{ ...primary, employmentType: nextValue }],
    });
    setEmploymentType(nextValue);
  }

  function handleChange(nextValue: IncomeType) {
    if (!isNonWorkingEmploymentType(nextValue) || !isPrimaryIncome) {
      setEmploymentType(nextValue);
      return;
    }

    const incomes: unknown[] = form.getFieldValue('incomes') ?? [];

    if (incomes.length <= 1) {
      setEmploymentType(nextValue);
      return;
    }

    modal.confirm({
      title: formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.unemployedConfirm.title' }),
      content: formatMessage(
        { id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.unemployedConfirm.content' },
        { employmentType: formatMessage({ id: EMPLOYMENT_TYPE_LABEL_IDS[nextValue] }) },
      ),
      okText: formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.unemployedConfirm.ok' }),
      onOk: () => applyNonWorkingPrimaryIncome(nextValue),
    });
  }

  return (
    <Select
      showSearch
      optionFilterProp='label'
      value={selectedEmploymentType}
      onChange={handleChange}
      options={employmentTypeOptions(includeNonWorking, formatMessage)}
      disabled={disabled}
    />
  );
}

export function AddEmploymentIncomeButton({
  isFormEditable,
  formatMessage,
}: {
  isFormEditable?: boolean;
  formatMessage: TypedFormatMessageFn;
}) {
  const form = Form.useFormInstance();
  const incomes: Array<{ employmentType?: IncomeType } | undefined> = Form.useWatch('incomes', form) ?? [];
  const isNonWorkingPrimary = isNonWorkingEmploymentType(incomes[0]?.employmentType);

  if (!isFormEditable) {
    return null;
  }

  if (incomes.length >= MAX_EMPLOYMENT_INCOMES && !isNonWorkingPrimary) {
    return null;
  }

  return (
    <Button
      type='dashed'
      className='w-full'
      icon={<PlusOutlined aria-hidden />}
      disabled={isNonWorkingPrimary}
      onClick={() => {
        form.setFieldsValue({
          incomes: [...incomes, {}],
        });
      }}
    >
      {formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.addEmploymentIncome.button' })}
    </Button>
  );
}
